import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArhamGuideProps, ArhamState, MascotMood } from './mascotTypes';
import { ArhamAnimation } from './ArhamAnimation';

const DEFAULT_MESSAGES = {
  en: {
    idle: "Hello! I'm Arham! Let's learn together!",
    wave: "Welcome! So excited to explore with you!",
    point: "Let me show you! Pay close attention!",
    celebrate: "Wow! You are super smart!",
    learn: "Let's discover something new today!",
    listen: "Listen closely to the sound!"
  },
  ur: {
    idle: "ہیلو! میں ارحم ہوں! آئیں مل کر سیکھیں!",
    wave: "خوش آمدید! آپ کے ساتھ سیکھ کر بہت خوشی ہوئی!",
    point: "آئیں دیکھیں! غور سے توجہ دیں!",
    celebrate: "واہ! آپ بہت ہوشیار ہیں!",
    learn: "آئیں آج کچھ نیا سیکھیں!",
    listen: "آواز کو غور سے سنیں!"
  },
  ar: {
    idle: "مرحباً! أنا أرحم! لنبدأ التعلم معاً!",
    wave: "أهلاً بك! أنا سعيد جداً بالتعلم معك!",
    point: "دعنا نلقي نظرة! انتبه جيداً!",
    celebrate: "رائع! أنت ذكي جداً!",
    learn: "دعنا نكتشف شيئاً جديداً اليوم!",
    listen: "استمع جيداً للصوت!"
  }
};

/**
 * Maps legacy Mascot mood props to the standardized ArhamState
 */
export function mapMoodToState(mood?: MascotMood): ArhamState {
  if (!mood) return 'idle';
  switch (mood) {
    case 'happy':
      return 'wave';
    case 'thinking':
    case 'sad':
      return 'point';
    case 'speaking':
      return 'listen';
    case 'celebrate':
      return 'celebrate';
    case 'idle':
    default:
      return 'idle';
  }
}

export const ArhamGuide: React.FC<ArhamGuideProps> = ({
  state: explicitState,
  mood,
  message,
  lang = 'en',
  onClick,
  className = '',
  showSpeechBubble = true
}) => {
  // Determine effective state (explicit state takes precedence over legacy mood)
  const effectiveState: ArhamState = useMemo(() => {
    if (explicitState) return explicitState;
    return mapMoodToState(mood);
  }, [explicitState, mood]);

  // Determine speech bubble text
  const speechText = useMemo(() => {
    if (message) return message;
    const langObj = DEFAULT_MESSAGES[lang] || DEFAULT_MESSAGES.en;
    return langObj[effectiveState] || langObj.idle;
  }, [message, lang, effectiveState]);

  const isRtl = lang === 'ur' || lang === 'ar';

  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 sm:gap-4 p-2 sm:p-3 select-none ${onClick ? 'cursor-pointer hover:opacity-95' : ''} ${className}`}
    >
      {/* Arham Mascot Animation */}
      <ArhamAnimation state={effectiveState} />

      {/* Mascot Speech Bubble */}
      {showSpeechBubble && (
        <AnimatePresence mode="wait">
          <motion.div
            key={`${effectiveState}-${speechText}`}
            initial={{ opacity: 0, scale: 0.9, x: isRtl ? 10 : -10 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className={`relative bg-white border-4 border-amber-400 rounded-3xl p-3.5 sm:p-4 shadow-lg max-w-xs sm:max-w-md ${
              isRtl ? 'font-serif' : 'font-sans'
            }`}
          >
            {/* Speech Bubble Arrow */}
            <div
              className={`absolute top-1/2 -translate-y-1/2 w-0 h-0 border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent ${
                isRtl
                  ? '-right-3.5 border-l-[14px] border-l-amber-400'
                  : '-left-3.5 border-r-[14px] border-r-amber-400'
              }`}
            />
            <div
              className={`absolute top-1/2 -translate-y-1/2 w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent ${
                isRtl
                  ? '-right-[10px] border-l-[11px] border-l-white'
                  : '-left-[10px] border-r-[11px] border-r-white'
              }`}
            />

            {/* Bubble Message Text */}
            <p
              className={`text-slate-700 font-extrabold text-sm sm:text-base leading-snug ${
                isRtl ? 'text-right' : 'text-left'
              }`}
            >
              {speechText}
            </p>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
};

export default ArhamGuide;
